import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealLine } from "../common/Reveal";
import { CTAButton } from "../common/CTAButton";
import { images } from "../../data/content";
import { ArrowDown } from "@phosphor-icons/react";

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
    const overlayY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section ref={ref} className="relative min-h-[100svh] bg-navy overflow-hidden grain" data-testid="hero">
            {/* Parallax background image */}
            <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
                <img src={images.heroWorkforce} alt="FRONTX integrated workforce across India" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-navy/80" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-navy/70" />
            </motion.div>

            <div className="absolute inset-0 z-[1] bg-grid opacity-60" />

            <motion.div style={{ y: overlayY }} className="relative z-10 mx-auto max-w-[1400px] px-5 lg:px-10 pt-36 lg:pt-44 pb-20">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="flex items-center gap-3 mb-8"
                >
                    <span className="h-px w-10 bg-brand" />
                    <span className="text-xs uppercase tracking-[0.28em] font-semibold text-brand">PAN India • Enterprise Ready</span>
                </motion.div>

                <h1 className="font-display font-extrabold text-white tracking-tight leading-[0.95] text-[2.6rem] sm:text-6xl lg:text-[5.5rem] max-w-5xl">
                    <RevealLine text="Integrated Manpower &" delay={0.15} className="block" />
                    <RevealLine text="Construction Solutions" delay={0.35} className="block text-brand" />
                    <RevealLine text="Across India" delay={0.55} className="block" />
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.7 }}
                    className="mt-8 max-w-2xl text-base lg:text-lg text-slate-300 leading-relaxed"
                >
                    FRONTX Integrated Services India Private Limited delivers scalable manpower, staffing,
                    workforce management and construction solutions for businesses, institutions and projects across India.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.05, duration: 0.7 }}
                    className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                    <CTAButton to="/contact#manpower" variant="primary" data-testid="hero-request-manpower">Request Manpower</CTAButton>
                    <CTAButton to="/contact#construction" variant="outline" data-testid="hero-project-quote">Get a Project Quote</CTAButton>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.8 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-slate-400"
            >
                <span className="text-[10px] uppercase tracking-[0.25em]">Scroll</span>
                <ArrowDown size={18} className="animate-bounce" />
            </motion.div>
        </section>
    );
};
